import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { siteInfo } from '@/mocks/site';
import { categories } from '@/mocks/categories';

export default function SiteHeader() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [columnOpen, setColumnOpen] = useState(false);
  const columnRef = useRef<HTMLDivElement | null>(null);

  const isHome = location.pathname === '/';
  const transparent = isHome && !scrolled && !mobileOpen;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setColumnOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (columnRef.current && !columnRef.current.contains(event.target as Node)) {
        setColumnOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const linkBase =
    'relative text-sm transition-colors duration-200 whitespace-nowrap py-2';
  const linkIdle = transparent ? 'text-foreground-200 hover:text-primary-400' : 'text-foreground-400 hover:text-primary-400';

  return (
    <header
      className={[
        'fixed left-0 top-0 z-50 w-full transition-all duration-300',
        transparent
          ? 'bg-transparent'
          : 'border-b border-background-200/80 bg-background-50/92 backdrop-blur-md',
      ].join(' ')}
    >
      <div className="mx-auto w-full px-4 md:px-10">
        <div className="flex h-16 items-center justify-between gap-4 md:h-20">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-gradient-to-br from-primary-400 to-accent-500 text-background-950">
              <i className="ri-moon-foggy-line text-lg"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg font-bold tracking-tight text-foreground-50">
                {siteInfo.name}
              </span>
              <span className="mt-1 hidden text-[10px] tracking-[0.22em] text-primary-400 sm:block">
                NIGHT SCOOP
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-6 lg:flex" aria-label="主导航">
            <NavLink
              to="/"
              className={({ isActive }) =>
                `${linkBase} ${isActive ? 'text-primary-400' : linkIdle}`
              }
            >
              首页
            </NavLink>

            <div className="relative" ref={columnRef}>
              <button
                type="button"
                onClick={() => setColumnOpen((prev) => !prev)}
                className={`${linkBase} ${columnOpen || location.pathname.startsWith('/category') ? 'text-primary-400' : linkIdle} flex cursor-pointer items-center gap-1`}
                aria-expanded={columnOpen}
                aria-haspopup="true"
              >
                栏目
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform ${columnOpen ? 'rotate-180' : ''}`}
                ></i>
              </button>
              {columnOpen ? (
                <div className="absolute left-1/2 top-full w-[440px] -translate-x-1/2 pt-3">
                  <div className="grid grid-cols-2 gap-1 rounded-lg border border-background-200 bg-background-100 p-3">
                    {categories.map((item) => (
                      <Link
                        key={item.slug}
                        to={`/category/${item.slug}`}
                        className="group flex items-start gap-3 rounded-md px-3 py-2.5 transition-colors hover:bg-background-200"
                      >
                        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-200 text-primary-400 transition-colors group-hover:bg-primary-500 group-hover:text-background-950">
                          <i className={`${item.icon} text-base`}></i>
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-foreground-100">
                            {item.name}
                          </span>
                          <span className="mt-0.5 block truncate text-xs text-foreground-500">
                            {item.count} 条已发
                          </span>
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            <NavLink
              to="/faq"
              className={({ isActive }) => `${linkBase} ${isActive ? 'text-primary-400' : linkIdle}`}
            >
              常见问题
            </NavLink>
            <NavLink
              to="/about"
              className={({ isActive }) => `${linkBase} ${isActive ? 'text-primary-400' : linkIdle}`}
            >
              关于我们
            </NavLink>
          </nav>

          <div className="flex items-center gap-2.5">
            <Link
              to="/contact"
              className="hidden cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-950 transition-colors hover:bg-primary-400 md:inline-flex"
            >
              <i className="ri-send-plane-fill text-sm"></i>
              我要投稿
            </Link>
            <button
              type="button"
              onClick={() => setMobileOpen((prev) => !prev)}
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300/70 bg-background-100/60 text-foreground-200 lg:hidden"
              aria-label="打开菜单"
              aria-expanded={mobileOpen}
            >
              <i className={mobileOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen ? (
        <div className="border-t border-background-200 bg-background-50 lg:hidden">
          <div className="mx-auto w-full px-4 py-5 md:px-10">
            <nav className="flex flex-col" aria-label="移动端导航">
              <Link to="/" className="border-b border-background-200/70 py-3 text-sm text-foreground-200">
                首页
              </Link>
              <Link to="/faq" className="border-b border-background-200/70 py-3 text-sm text-foreground-200">
                常见问题
              </Link>
              <Link to="/about" className="border-b border-background-200/70 py-3 text-sm text-foreground-200">
                关于我们
              </Link>
              <Link to="/contact" className="border-b border-background-200/70 py-3 text-sm text-foreground-200">
                联系我们
              </Link>
            </nav>
            <p className="mb-3 mt-5 text-xs tracking-[0.2em] text-foreground-500">全部栏目</p>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((item) => (
                <Link
                  key={item.slug}
                  to={`/category/${item.slug}`}
                  className="flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-200"
                >
                  <i className={`${item.icon} text-primary-400`}></i>
                  {item.name}
                </Link>
              ))}
            </div>
            <Link
              to="/contact"
              className="mt-5 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-950"
            >
              <i className="ri-send-plane-fill"></i>
              我要投稿
            </Link>
          </div>
        </div>
      ) : null}
    </header>
  );
}